import React, { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TrafficLight from "@/components/TrafficLight";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { toast } from "sonner";
import {
  ShieldCheck, Calendar, Camera, X, Trash2,
  Image as ImageIcon, AlertTriangle, AlertOctagon, CheckCircle2, Building2,
} from "lucide-react";
import VaultHeaderMenu from "@/components/vault/VaultHeaderMenu";
import TrainingMenu from "@/components/TrainingMenu";
import JoinCompanyModal from "@/components/vault/JoinCompanyModal";

export default function MyVault() {
  const { user } = useAuth();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [certTypes, setCertTypes] = useState([]);

  const load = async () => {
    if (!user?.employee_id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/certifications", { params: { employee_id: user.employee_id } });
      setCerts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/meta/cert-types").then(r => setCertTypes(r.data.types || []));
  }, [user?.employee_id]);

  const counts = {
    expired: certs.filter(c => c.status === "expired").length,
    expiring: certs.filter(c => c.status === "expiring_soon").length,
    current: certs.filter(c => c.status === "current").length,
  };

  const removeCert = async (c) => {
    if (!window.confirm(`Delete "${c.cert_type}"?`)) return;
    try {
      await api.delete(`/certifications/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground pb-16">
      {/* page header (chrome lives in global Navbar) */}
      <div className="max-w-2xl mx-auto px-4 pt-6 md:pt-8">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">My Vault</div>
            <div className="font-heading text-3xl md:text-4xl mt-0.5 truncate" data-testid="vault-user-name">
              {user?.name?.toUpperCase()}
            </div>
            {user?.company_id ? (
              <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <Building2 className="w-3 h-3" strokeWidth={3} /> Company linked
              </div>
            ) : (
              <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <ShieldCheck className="w-3 h-3 text-safety-yellow" strokeWidth={3} /> Personal Vault
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <TrainingMenu size="md" />
            <VaultHeaderMenu
              onAddCertification={() => setShowAdd(true)}
              onJoinCompany={() => setShowJoin(true)}
              showJoinCompany={!user?.company_id}
            />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 grid-bg">
        {/* status strip */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatBlock Icon={CheckCircle2} label="Current" n={counts.current} cls="bg-status-current text-white" />
          <StatBlock Icon={AlertTriangle} label="Expiring" n={counts.expiring} cls="bg-status-expiring text-black" />
          <StatBlock Icon={AlertOctagon} label="Expired" n={counts.expired} cls="bg-status-expired text-white" />
        </div>

        {/* certs list */}
        {loading ? (
          <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Loading...</div>
        ) : certs.length === 0 ? (
          <div className="brutal-card p-10 text-center">
            <ShieldCheck className="w-14 h-14 mx-auto text-safety-yellow" strokeWidth={1.8} />
            <div className="font-heading text-2xl mt-3">No certifications yet</div>
            <div className="text-sm text-muted-foreground mt-2">Tap the yellow <b>+</b> in the header to add your first one.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="vault-list">
            {certs.map((c) => <CertCard key={c.id} cert={c} onView={() => setViewing(c)} onDelete={() => removeCert(c)} onRefresh={load} />)}
          </div>
        )}
      </div>

      {showAdd && (
        <AddCertModal certTypes={certTypes} employeeId={user?.employee_id}
          onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />
      )}

      {showJoin && (
        <JoinCompanyModal
          onClose={() => setShowJoin(false)}
          onJoined={() => { setShowJoin(false); load(); }}
        />
      )}

      {viewing && <PhotoViewer cert={viewing} onClose={() => setViewing(null)} onRefresh={load} />}
    </div>
  );
}

function StatBlock({ Icon, label, n, cls }) {
  return (
    <div className={`border-2 border-black px-3 py-3 ${cls}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest">
        <Icon className="w-3.5 h-3.5" strokeWidth={3} /> {label}
      </div>
      <div className="font-heading text-3xl mt-1 leading-none">{n}</div>
    </div>
  );
}

function CertCard({ cert, onView, onDelete, onRefresh }) {
  return (
    <div className="brutal-card p-4" data-testid={`vault-cert-${cert.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-safety-yellow" /> {cert.issuer || "Certification"}
          </div>
          <div className="font-heading text-2xl leading-tight mt-1">{cert.cert_type}</div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-2 inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> Expires {cert.expiration_date}
          </div>
        </div>
        <TrafficLight status={cert.status} size="md" />
      </div>
      <div className="mt-3 flex gap-2 flex-wrap">
        <button data-testid={`view-cert-${cert.id}`} onClick={onView}
          className="h-12 px-3 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2">
          {cert.photo_path ? <ImageIcon className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
          {cert.photo_path ? "View Card" : "Add Photo"}
        </button>
        <button data-testid={`delete-cert-${cert.id}`} onClick={onDelete}
          className="h-12 w-12 border-2 border-status-expired text-status-expired hover:bg-status-expired hover:text-white transition-colors inline-flex items-center justify-center">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function AddCertModal({ certTypes, employeeId, onClose, onCreated }) {
  const [form, setForm] = useState({ cert_type: "", custom_name: "", expiration_date: "", issuer: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const isCustom = form.cert_type === "__custom__";

  const submit = async (e) => {
    e.preventDefault();
    const cert_type = isCustom ? form.custom_name.trim() : form.cert_type;
    if (!cert_type) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/certifications", {
        employee_id: employeeId,
        cert_type,
        expiration_date: form.expiration_date,
        issuer: form.issuer || null,
      });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/certifications/${data.id}/photo`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Certification added");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end md:items-center justify-center p-0 md:p-6">
      <div className="bg-background brutal-card w-full md:max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b-2 border-foreground dark:border-safety-yellow p-4 sticky top-0 bg-background">
          <div className="font-heading text-2xl">Add Certification</div>
          <button onClick={onClose} data-testid="add-cert-close"
            className="h-12 w-12 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black">
            <X className="w-5 h-5 mx-auto" strokeWidth={3} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Certification Title</label>
            <select data-testid="vault-cert-type" required className="industrial-input"
              value={form.cert_type}
              onChange={(e) => setForm({ ...form, cert_type: e.target.value, custom_name: e.target.value === "__custom__" ? form.custom_name : "" })}>
              <option value="">Select type...</option>
              {certTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              <option value="__custom__">Custom...</option>
            </select>
            {isCustom && (
              <input data-testid="vault-cert-custom" required autoFocus
                placeholder="Enter Custom Certification Name"
                className="industrial-input mt-2" value={form.custom_name}
                onChange={(e) => setForm({ ...form, custom_name: e.target.value })} />
            )}
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Expiration Date</label>
            <input data-testid="vault-cert-exp" required type="date" className="industrial-input"
              value={form.expiration_date}
              onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Issuer (optional)</label>
            <input data-testid="vault-cert-issuer" className="industrial-input" value={form.issuer}
              onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="e.g. OSHA" />
          </div>

          {file ? (
            <div className="border-2 border-safety-yellow bg-safety-yellow/10 px-4 min-h-[48px] flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 truncate">
                <ImageIcon className="w-4 h-4 text-safety-yellow" /> Photo Attached · {file.name}
              </span>
              <button type="button" data-testid="vault-photo-remove"
                onClick={() => setFile(null)}
                className="h-8 w-8 inline-flex items-center justify-center hover:bg-safety-yellow hover:text-black flex-shrink-0"
                aria-label="Remove photo">
                <X className="w-4 h-4" strokeWidth={3} />
              </button>
            </div>
          ) : (
            <label data-testid="vault-cert-photo-attach"
              className="cursor-pointer w-full inline-flex items-center justify-center gap-2 bg-transparent border-2 border-dashed border-foreground/50 dark:border-safety-yellow/60 px-4 min-h-[48px] text-xs font-bold uppercase tracking-wider hover:bg-safety-yellow/10 hover:border-safety-yellow transition-colors">
              <Camera className="w-4 h-4" strokeWidth={2.5} />
              Attach Photo (Optional)
              <input data-testid="vault-cert-photo" type="file" accept="image/*" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          )}

          <button data-testid="vault-cert-save" disabled={saving} type="submit" className="btn-primary w-full">
            {saving ? "Saving..." : "Save Certification"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PhotoViewer({ cert, onClose, onRefresh }) {
  useBodyScrollLock(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    if (cert.photo_path) {
      api.get("/files", { params: { path: cert.photo_path }, responseType: "blob" })
        .then((r) => {
          if (cancelled) return;
          createdUrl = URL.createObjectURL(r.data);
          setBlobUrl(createdUrl);
        })
        .catch(() => toast.error("Could not load image"));
    }
    return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [cert.photo_path]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/certifications/${cert.id}/photo`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      onRefresh();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} data-testid="viewer-close"
        className="absolute top-4 right-4 h-12 w-12 bg-safety-yellow text-black border-2 border-black flex items-center justify-center z-10">
        <X className="w-6 h-6" strokeWidth={3} />
      </button>
      <div className="max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="brutal-card bg-background p-5">
          <div className="font-heading text-3xl">{cert.cert_type}</div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-1">
            Expires {cert.expiration_date}
          </div>
          <div className="mt-4 border-2 border-foreground dark:border-safety-yellow bg-black flex items-center justify-center min-h-[280px]">
            {blobUrl ? (
              <img src={blobUrl} alt={cert.cert_type} className="max-h-[60vh] w-auto object-contain" />
            ) : (
              <label className="cursor-pointer text-center py-12 px-6 block w-full">
                <input type="file" accept="image/*" className="hidden" data-testid="viewer-upload"
                  onChange={(e) => upload(e.target.files?.[0])} />
                <Camera className="w-16 h-16 mx-auto text-safety-yellow" strokeWidth={1.5} />
                <div className="font-heading text-2xl mt-3 text-white">
                  {uploading ? "Uploading..." : "Add Photo of Card"}
                </div>
                <div className="text-xs text-muted-foreground mt-2 font-bold uppercase tracking-wider">
                  Tap to pick an image
                </div>
              </label>
            )}
          </div>
          {blobUrl && (
            <label className="block mt-3 cursor-pointer">
              <input type="file" accept="image/*" className="hidden" data-testid="viewer-replace"
                onChange={(e) => upload(e.target.files?.[0])} />
              <div className="btn-outline w-full">Replace Photo</div>
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
